import React, { useEffect, useState, useRef } from 'react';
import { suppliesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Loader2, Camera, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

/**
 * EquipmentPhoto — inline thumbnail loader for a type or asset photo.
 * Handles the auth-required blob fetch and falls back to the type
 * photo when the asset has no picture of its own.
 *
 * Props:
 *   assetId?: string     — when provided, tries the asset photo first
 *   typeId?:  string     — used as fallback when the asset has none
 *   size:    number      — px dimension of the square thumb (default 40)
 *   onEmpty?: React node — rendered when neither photo exists
 */
export function EquipmentPhoto({ assetId, typeId, size = 40, className = '', onEmpty = null, refreshKey = 0 }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setSrc(null);
      const tryFetch = async (fn) => {
        try {
          const res = await fn();
          if (cancelled) return null;
          return URL.createObjectURL(res.data);
        } catch {
          return null;
        }
      };
      let url = null;
      if (assetId) url = await tryFetch(() => suppliesAPI.assetPhotoBlob(assetId));
      if (!url && typeId) url = await tryFetch(() => suppliesAPI.typePhotoBlob(typeId));
      if (!cancelled) {
        objectUrl = url;
        setSrc(url);
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, typeId, refreshKey]);

  const box = `rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden ${className}`;
  const style = { width: size, height: size };
  if (loading) {
    return <div className={box} style={style}><Loader2 size={14} className="animate-spin text-slate-400" /></div>;
  }
  if (src) {
    return <img src={src} alt="" className={box} style={{ ...style, objectFit: 'cover' }} />;
  }
  return <div className={box} style={style}>{onEmpty || <ImageIcon size={16} className="text-slate-300" />}</div>;
}

/**
 * EquipmentPhotoUploader — larger square with Upload / Remove controls,
 * for use inside Edit dialogs. Accepts either `assetId` OR `typeId`.
 *
 * Calls the correct upload / delete endpoint and re-fetches the blob
 * after each mutation via `refreshKey`.
 */
export function EquipmentPhotoUploader({ assetId, typeId, disabled = false, testId = 'equipment-photo-uploader' }) {
  const inputRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(false);

  if (!assetId && !typeId) {
    return (
      <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-500">
        Save this record first to enable photo upload.
      </div>
    );
  }

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be ≤ 10 MB');
      return;
    }
    setBusy(true);
    try {
      if (assetId) {
        await suppliesAPI.uploadAssetPhoto(assetId, file);
      } else {
        await suppliesAPI.uploadTypePhoto(typeId, file);
      }
      toast.success('Photo updated');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this photo?')) return;
    setBusy(true);
    try {
      if (assetId) {
        await suppliesAPI.deleteAssetPhoto(assetId);
      } else {
        await suppliesAPI.deleteTypePhoto(typeId);
      }
      toast.success('Photo removed');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid={testId}>
      <EquipmentPhoto assetId={assetId} typeId={typeId} size={96} refreshKey={refreshKey} />
      <div className="space-y-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFile}
          className="hidden"
          data-testid={`${testId}-input`}
        />
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePick}
            disabled={disabled || busy}
            data-testid={`${testId}-upload`}
          >
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Camera size={14} className="mr-1" />}
            {assetId ? 'Upload Asset Photo' : 'Upload Type Photo'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={disabled || busy}
            className="text-rose-600"
          >
            <Trash2 size={14} />
          </Button>
        </div>
        <p className="text-xs text-slate-500 max-w-[240px]">
          JPG / PNG / WebP up to 10 MB. Auto-cropped to a square.
        </p>
      </div>
    </div>
  );
}
